import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiActivity, FiPackage, FiDollarSign, FiCheckCircle } from 'react-icons/fi';
import { formatINR } from '../../utils/currency';

const KPIs = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [kpiData, setKpiData] = useState({
    production: {
      totalUnits: 125000,
      targetUnits: 150000,
      efficiency: 83.3,
      trend: 'up'
    },
    sales: {
      revenue: 2500000,
      targetRevenue: 3000000,
      growth: 12.5,
      trend: 'up'
    },
    compliance: {
      auditScore: 95,
      targetScore: 90,
      violations: 2,
      trend: 'up'
    }
  });

  const productionKPIs = [
    { label: 'Total Units Produced', value: kpiData.production.totalUnits.toLocaleString(), target: kpiData.production.targetUnits.toLocaleString(), unit: 'units' },
    { label: 'Production Efficiency', value: `${kpiData.production.efficiency}%`, target: '100%', unit: '%' },
    { label: 'On-Time Delivery', value: '92%', target: '95%', unit: '%' },
    { label: 'Quality Pass Rate', value: '98.5%', target: '99%', unit: '%' }
  ];

  const salesKPIs = [
    { label: 'Total Revenue', value: formatINR(kpiData.sales.revenue), target: formatINR(kpiData.sales.targetRevenue), unit: 'INR' },
    { label: 'Sales Growth', value: `${kpiData.sales.growth}%`, target: '15%', unit: '%' },
    { label: 'Orders Fulfilled', value: '1,234', target: '1,500', unit: 'orders' },
    { label: 'Average Order Value', value: formatINR(2025), target: formatINR(2200), unit: 'INR' }
  ];

  const complianceKPIs = [
    { label: 'Audit Score', value: `${kpiData.compliance.auditScore}%`, target: `${kpiData.compliance.targetScore}%`, unit: '%' },
    { label: 'Regulatory Violations', value: String(kpiData.compliance.violations), target: '0', unit: 'violations' },
    { label: 'Documentation Compliance', value: '98%', target: '100%', unit: '%' },
    { label: 'Training Completion', value: '94%', target: '100%', unit: '%' }
  ];

  const renderKPICard = (kpis, title, icon, color) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
      </div>
      <div className="space-y-4">
        {kpis.map((kpi, index) => {
          const valueStr = String(kpi.value);
          const targetStr = String(kpi.target);
          const valueNum = parseFloat(valueStr.replace(/[^0-9.]/g, ''));
          const targetNum = parseFloat(targetStr.replace(/[^0-9.]/g, ''));
          const percentage = targetNum > 0 ? (valueNum / targetNum) * 100 : 0;
          const isAchieved = percentage >= 100;
          
          return (
            <div key={index}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">{kpi.label}</span>
                <span className={`text-sm font-bold ${isAchieved ? 'text-green-600' : 'text-orange-600'}`}>
                  {kpi.value} / {kpi.target}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${isAchieved ? 'bg-green-600' : 'bg-orange-600'}`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiActivity className="text-blue-600" />
            Key Performance Indicators
          </h1>
          <p className="text-gray-600 mt-1">Production, Sales & Compliance Metrics</p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {renderKPICard(
          productionKPIs,
          'Production KPIs',
          <FiPackage className="w-6 h-6 text-blue-600" />,
          'bg-blue-100'
        )}
        {renderKPICard(
          salesKPIs,
          'Sales KPIs',
          <FiDollarSign className="w-6 h-6 text-green-600" />,
          'bg-green-100'
        )}
        {renderKPICard(
          complianceKPIs,
          'Compliance KPIs',
          <FiCheckCircle className="w-6 h-6 text-purple-600" />,
          'bg-purple-100'
        )}
      </div>

      <div className="mt-6 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Performance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Production</span>
              <FiTrendingUp className="text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-2">{kpiData.production.efficiency}%</p>
            <p className="text-xs text-gray-600 mt-1">Efficiency Rate</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Sales</span>
              <FiTrendingUp className="text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-2">{kpiData.sales.growth}%</p>
            <p className="text-xs text-gray-600 mt-1">Growth Rate</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Compliance</span>
              <FiTrendingUp className="text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-800 mt-2">{kpiData.compliance.auditScore}%</p>
            <p className="text-xs text-gray-600 mt-1">Audit Score</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIs;

